import { createTheme } from '@mui/material/styles';

/**
 * Flat, high-contrast Material UI theme (Aplopes).
 *
 * Design rules baked in globally:
 *  - No ripple effect anywhere (buttons, icon buttons, list items, tabs...).
 *  - Solid / flat components: no elevation shadows, no gradients.
 *  - Very small corner radius (subtle rounding, not square, not "bubbly").
 *  - No borders / outlines on components.
 *  - High-contrast palette: white, black, Aplopes blue.
 *  - Text fields have NO floating-label animation (the label sits above the
 *    field statically and never "shrinks out" of the input).
 */

// Aplopes brand blue (from the original design system).
const BLUE = '#2b6cb0';
const BLUE_DARK = '#245a96';
const BLUE_ACTIVE = '#1e4d82';
const BLACK = '#111111';
const WHITE = '#ffffff';
const SURFACE_MUTED = '#f2f2f2';
const BORDER_FLAT = '#e0e0e0';
const TEXT_MUTED = '#555555';

// Just a small corner radius — keeps the flat look, softens the edges.
const RADIUS = 4;

const theme = createTheme({
  shape: {
    borderRadius: RADIUS,
  },
  palette: {
    mode: 'light',
    primary: {
      main: BLUE,
      dark: BLUE_DARK,
      contrastText: WHITE,
    },
    secondary: {
      main: BLACK,
      contrastText: WHITE,
    },
    background: {
      default: WHITE,
      paper: WHITE,
    },
    text: {
      primary: BLACK,
      secondary: TEXT_MUTED,
    },
    divider: BORDER_FLAT,
  },
  typography: {
    // Roboto is self-hosted via @fontsource (imported in index.tsx); the rest
    // are graceful fallbacks while the font loads / if it fails.
    fontFamily:
      "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
    button: {
      textTransform: 'none',
      fontWeight: 700,
      letterSpacing: 0,
    },
  },
  // Flatten the whole elevation scale so nothing ever casts a shadow.
  shadows: Array(25).fill('none') as unknown as ReturnType<
    typeof createTheme
  >['shadows'],
  components: {
    // Global scroll behavior: kill the rubber-band/overscroll bounce only.
    // (Do NOT set html height:100% / overflow:hidden — that traps the page
    // and breaks normal wheel scrolling.)
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          overscrollBehavior: 'none',
        },
        body: {
          overscrollBehavior: 'none',
          minHeight: '100vh',
        },
        '#root': {
          minHeight: '100vh',
        },
      },
    },
    // Kill the ripple everywhere via default props on the base ButtonBase.
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          borderRadius: RADIUS,
          boxShadow: 'none',
          transition:
            'background-color 120ms ease, color 120ms ease, border-color 120ms ease',
          '&:hover': { boxShadow: 'none' },
          '&:active': { boxShadow: 'none' },
          '&:focus-visible': { outline: 'none' },
        },
      },
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            backgroundColor: BLUE,
            color: WHITE,
            '&:hover': { backgroundColor: BLUE_DARK },
            '&:active': { backgroundColor: BLUE_ACTIVE },
          },
        },
        {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            backgroundColor: BLACK,
            color: WHITE,
            '&:hover': { backgroundColor: '#222222' },
          },
        },
        {
          // "outlined" without a real border: flat surface on muted bg instead.
          props: { variant: 'outlined' },
          style: {
            border: 'none',
            backgroundColor: SURFACE_MUTED,
            color: BLACK,
            '&:hover': { border: 'none', backgroundColor: '#e6e6e6' },
          },
        },
        {
          props: { variant: 'text' },
          style: { '&:hover': { backgroundColor: SURFACE_MUTED } },
        },
      ],
    },
    MuiIconButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          borderRadius: RADIUS,
          '&:focus-visible': { outline: 'none' },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none', boxShadow: 'none' },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { boxShadow: 'none', border: 'none' },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'default' },
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderRadius: 0,
          backgroundColor: WHITE,
          color: BLACK,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: RADIUS, boxShadow: 'none' },
      },
    },
    MuiMenu: {
      defaultProps: { disableScrollLock: true },
      styleOverrides: {
        paper: { borderRadius: RADIUS, boxShadow: 'none', border: `1px solid ${BORDER_FLAT}` },
      },
    },
    MuiMenuItem: {
      defaultProps: { disableRipple: true },
    },
    MuiListItemButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: { borderRadius: RADIUS },
      },
    },
    MuiTab: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 700 },
      },
    },
    MuiTabs: {
      defaultProps: { slotProps: { indicator: { style: { transition: 'none' } } } },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: RADIUS },
      },
    },
    MuiToggleButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          borderRadius: RADIUS,
          border: 'none',
          textTransform: 'none',
        },
      },
    },
    MuiCheckbox: { defaultProps: { disableRipple: true } },
    MuiRadio: { defaultProps: { disableRipple: true } },
    MuiSwitch: { defaultProps: { disableRipple: true } },

    // ---- Text fields: static label, no floating animation, flat look ----
    MuiTextField: {
      defaultProps: {
        variant: 'filled' as const,
        slotProps: { inputLabel: { shrink: true } },
      },
    },
    MuiInputLabel: {
      defaultProps: { shrink: true },
      styleOverrides: {
        root: {
          position: 'static',
          transform: 'none',
          transition: 'none',
          pointerEvents: 'auto',
          maxWidth: '100%',
          color: BLACK,
          fontWeight: 600,
          fontSize: '0.85rem',
          marginBottom: 6,
          '&.Mui-focused': { color: BLACK },
        },
      },
    },
    MuiFilledInput: {
      defaultProps: { disableUnderline: true },
      styleOverrides: {
        root: {
          borderRadius: RADIUS,
          backgroundColor: SURFACE_MUTED,
          border: 'none',
          transition: 'background-color 120ms ease, box-shadow 120ms ease',
          '&:hover': { backgroundColor: '#ececec' },
          '&.Mui-focused': {
            backgroundColor: WHITE,
            boxShadow: `inset 0 0 0 2px ${BLUE}`,
          },
          '&.Mui-error.Mui-focused': {
            boxShadow: 'inset 0 0 0 2px #d32f2f',
          },
        },
        input: {
          paddingTop: 12,
          paddingBottom: 12,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS,
          backgroundColor: SURFACE_MUTED,
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '&.Mui-focused': {
            backgroundColor: WHITE,
            boxShadow: `inset 0 0 0 2px ${BLUE}`,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
        },
      },
    },
    MuiInput: {
      defaultProps: { disableUnderline: true },
    },
    MuiSelect: {
      defaultProps: { variant: 'filled' as const },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { borderRadius: RADIUS },
      },
    },
  },
});

export default theme;
